/**
 * Utilitários compartilhados da agenda do Mural.
 *
 * Vivem fora dos componentes porque a mesma conversão (data local sem fuso,
 * status do slot, cor tonal) é usada pela grade, pela lista de próximas aulas e
 * pelo drawer — e três cópias divergiriam na primeira correção.
 */

/**
 * `new Date('2026-08-11')` é interpretado como UTC e, a oeste de Greenwich,
 * volta como dia 10. Toda data que vem do backend é `Y-m-d` puro, então tem de
 * ser montada componente a componente.
 */
export function parseDate(value) {
  const [year, month, day] = String(value).split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function toDateString(date) {
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** Segunda-feira da semana da data (a grade do app começa na segunda). */
export function mondayOf(date) {
  const result = new Date(date)
  result.setHours(0, 0, 0, 0)
  result.setDate(result.getDate() - ((result.getDay() + 6) % 7))
  return result
}

export function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()
}

export function minutesOf(time) {
  const [hour, minute] = String(time).split(':').map(Number)
  return hour * 60 + (minute || 0)
}

/** Hex do design system em rgba, para o preenchimento tonal do bloco. */
export function tint(hex, alpha = 0.13) {
  const clean = String(hex || '#8BB2DB').replace('#', '')
  const r = parseInt(clean.substring(0, 2), 16)
  const g = parseInt(clean.substring(2, 4), 16)
  const b = parseInt(clean.substring(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Escurece a cor da modalidade para virar texto legível sobre o próprio tint.
 * A paleta do design system é clara demais para ser lida direto.
 */
export function inkOf(hex) {
  const clean = String(hex || '#8BB2DB').replace('#', '')
  const channel = index => Math.round(parseInt(clean.substr(index, 2), 16) * 0.62)
  return `rgb(${channel(0)}, ${channel(2)}, ${channel(4)})`
}

/**
 * Situação de uma ocorrência, na mesma ordem de precedência da agenda da aula:
 * cancelada ganha de bloqueada, que ganha de lotada.
 */
export function slotStatus(slot) {
  if (slot.status === 'CA') return 'cancelled'
  if (slot.blocked) return 'blocked'
  if (slot.capacity !== null && slot.occupied >= slot.capacity) return 'full'
  return 'scheduled'
}

export function occupancyPercent(slot) {
  if (!slot.capacity) return 0
  return Math.min(100, Math.round((slot.occupied / slot.capacity) * 100))
}

/** "3/8", ou "3/∞" quando a turma não tem limite. */
export function occupancyLabel(slot) {
  if (slot.status === 'CA') return '—'
  return `${slot.occupied}/${slot.capacity === null ? '∞' : slot.capacity}`
}

/** A ocorrência já passou? Usado só para esmaecer — não muda status nenhum. */
export function isElapsed(date, startTime) {
  const day = parseDate(date)
  const [hour, minute] = String(startTime).split(':').map(Number)
  day.setHours(hour, minute || 0, 0, 0)
  return day.getTime() < Date.now()
}

export const WEEKDAY_SHORT = ['seg', 'ter', 'qua', 'qui', 'sex', 'sáb', 'dom']

export const MONTH_NAMES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
]

export function longDateLabel(date) {
  const weekdays = [
    'domingo', 'segunda-feira', 'terça-feira', 'quarta-feira',
    'quinta-feira', 'sexta-feira', 'sábado',
  ]
  return `${weekdays[date.getDay()]}, ${date.getDate()} de ${MONTH_NAMES[date.getMonth()]}`
}
